import { $getRoot } from "lexical";
import { useCallback } from "react";
import { $isSurveyLinkNode } from "../nodes/survey-link-node";
import { countTwilioCodeUnits } from "./gsm-encoding";
import { urlRegex } from "./link-matchers";

import type { EditorState } from "lexical";

const TRACKING_LINK_LENGTH = 55;
// All links are shortened to `https://ce-l.ink/{7-char id}` (17 + 7 = 24) when sent.
export const SHORTENED_LINK_LENGTH = 24;
export const MEDIAN_MERGE_TAG_LENGTH = 9;

// A single-character GSM-7 placeholder used to pad fixed-length substitutions
// (links, merge tags) to their expected size without influencing the encoding
// detection. Regular ASCII "a" is 1 code unit in both GSM-7 and UCS-2.
const GSM_PLACEHOLDER = "a";

/**
 * Build a string that represents the message *as Twilio will encode it*, then
 * measure it through `countTwilioCodeUnits` so the count reflects the true
 * encoding cost.
 */
export const getCharacterCount = (state: EditorState): number =>
  state.read(() => {
    const textNodes = $getRoot().getAllTextNodes();

    if (!textNodes || textNodes.length === 0) return 0;

    const parts: string[] = [];
    let nonMergeTagNodeCount = 0;

    for (const node of textNodes) {
      if (node.getType() === "merge-tag-v2") {
        parts.push(GSM_PLACEHOLDER.repeat(MEDIAN_MERGE_TAG_LENGTH));
        continue;
      }

      nonMergeTagNodeCount++;

      // A survey link's visible child is the literal text "survey_link", but it
      // is shortened when sent, so count its shortened length rather than the label.
      if ($isSurveyLinkNode(node.getParent())) {
        parts.push(GSM_PLACEHOLDER.repeat(SHORTENED_LINK_LENGTH));
        continue;
      }

      const text = node
        .getTextContent()
        .replace(urlRegex, () => GSM_PLACEHOLDER.repeat(TRACKING_LINK_LENGTH));
      parts.push(text);
    }

    const lineBreaks = nonMergeTagNodeCount > 0 ? nonMergeTagNodeCount - 1 : 0;
    const effectiveText = parts.join("") + "\n".repeat(lineBreaks);

    return countTwilioCodeUnits(effectiveText);
  });

export const useGetCharacterCount = (): ((state: EditorState) => number) =>
  useCallback((state: EditorState) => getCharacterCount(state), []);
